// src/services/api.ts

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:5001';

// Types matching your backend responses
export interface HealthMetric {
  test_name: string;
  value: string;
  unit?: string;
  reference_range?: string;
  status?: 'Normal' | 'High' | 'Low' | 'Critical';
}

export interface PatientInfo {
  name?: string;
  age?: string;
  gender?: string;
  report_date?: string;
  report_id?: string;
}

export interface ProcessingInfo {
  file_type: string;
  processing_method: string;
  extraction_time?: number;
}

export interface ExtractResponse {
  success: boolean;
  message: string;
  data: {
    filename: string;
    file_type: string;
    processing_method: string;
    patient_info: PatientInfo;
    health_metrics: HealthMetric[];
    processing_info: ProcessingInfo;
    metrics_count: number;
    extracted_text?: string;
    report_id?: string;
  };
  timestamp: string;
}

export interface AsyncJobResponse {
  success: boolean;
  message: string;
  data: {
    job_id: string;
    user_id: string;
    status: string;
    stream_url: string;
    status_url: string;
    filename: string;
  };
  timestamp: string;
}

export interface JobStatusResponse {
  success: boolean;
  data: {
    job_id: string;
    status: 'queued' | 'processing' | 'completed' | 'failed';
    progress: number;
    current_stage: string;
    result?: {
      filename: string;
      file_type: string;
      patient_info: PatientInfo;
      health_metrics: HealthMetric[];
      processing_info: ProcessingInfo;
      metrics_count: number;
      report_id?: string;
    };
    result_data?: {
      filename: string;
      file_type: string;
      patient_info: PatientInfo;
      health_metrics: HealthMetric[];
      processing_info: ProcessingInfo;
      metrics_count: number;
      report_id?: string;
    };
    error?: string | {
      message: string;
      code: string;
      type: string;
    };
    created_at: string;
    updated_at: string;
  };
}

export interface ApiError {
  success: false;
  error: {
    message: string;
    type: string;
    timestamp: string;
  };
}

class ApiService {
  private baseUrl: string;
  private onUnauthorized?: () => void;

  constructor(baseUrl: string = API_BASE_URL) {
    this.baseUrl = baseUrl;
  }

  /**
   * Set callback for handling unauthorized errors
   */
  setUnauthorizedHandler(handler: () => void) {
    this.onUnauthorized = handler;
  }

  /**
   * Handle API response and check for 401 errors
   */
  private async handleResponse(response: Response): Promise<any> {
    // Handle 401 Unauthorized
    if (response.status === 401) {
      // Clear auth tokens
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');

      // Call unauthorized handler if set
      if (this.onUnauthorized) {
        this.onUnauthorized();
      }

      throw new Error('UNAUTHORIZED');
    }

    const data = await response.json();

    // Handle 403 Forbidden - Email Verification Required
    if (response.status === 403) {
      const errorMessage = data.error?.message || '';
      const errorType = data.error?.type || '';

      // Check if it's an email verification error
      if (errorType === 'email_verification_required' ||
        errorMessage.toLowerCase().includes('email verification') ||
        errorMessage.toLowerCase().includes('verify your email')) {

        // Clear tokens to force re-login
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('heal_user_data');

        // Call unauthorized handler to redirect to login
        if (this.onUnauthorized) {
          this.onUnauthorized();
        }

        throw new Error('EMAIL_VERIFICATION_REQUIRED: Your session is outdated. Please log out and log in again to refresh your authentication.');
      }

      // Other 403 errors (access denied, etc.)
      throw new Error(data.error?.message || 'Access denied');
    }

    if (!response.ok) {
      throw new Error(data.error?.message || `HTTP error! status: ${response.status}`);
    }

    return data;
  }

  /**
   * Get auth token from localStorage
   */
  private getAuthToken(): string | null {
    return localStorage.getItem('access_token');
  }

  /**
   * Get auth headers
   */
  private getAuthHeaders(): HeadersInit {
    const token = this.getAuthToken();
    return token ? { 'Authorization': `Bearer ${token}` } : {};
  }

  /**
   * Extract health metrics for preview (fast, no database save)
   */
  async extractHealthMetricsPreview(file: File, password?: string): Promise<ExtractResponse> {
    const formData = new FormData();
    formData.append('file', file);

    if (password) {
      formData.append('password', password);
    }

    try {
      const response = await fetch(`${this.baseUrl}/extract/preview`, {
        method: 'POST',
        headers: {
          ...this.getAuthHeaders()
        },
        body: formData,
      });

      const data = await this.handleResponse(response);

      if (!data.success) {
        throw new Error(data.error?.message || 'Processing failed');
      }

      return data;
    } catch (error) {
      console.error('API Error:', error);
      throw error;
    }
  }

  /**
   * Extract health metrics asynchronously (saves to database)
   */
  async extractHealthMetricsAsync(file: File, password?: string): Promise<AsyncJobResponse> {
    const formData = new FormData();
    formData.append('file', file);

    if (password) {
      formData.append('password', password);
    }

    try {
      const response = await fetch(`${this.baseUrl}/extract/async`, {
        method: 'POST',
        headers: {
          ...this.getAuthHeaders()
        },
        body: formData,
      });

      const data = await this.handleResponse(response);

      if (!data.success) {
        throw new Error(data.error?.message || 'Processing failed');
      }

      return data;
    } catch (error) {
      console.error('API Error:', error);
      throw error;
    }
  }

  /**
   * Get job status
   * Returns null if job not found (404) to allow polling to continue
   * Throws only on actual errors (auth, network, etc.)
   */
  async getJobStatus(jobId: string): Promise<JobStatusResponse | null> {
    try {
      const response = await fetch(`${this.baseUrl}/job/${jobId}`, {
        method: 'GET',
        headers: {
          ...this.getAuthHeaders()
        }
      });

      // Handle 404 - job not found yet (backend might be cold starting)
      if (response.status === 404) {
        console.warn(`[Job Polling] Job ${jobId} not found yet (404) - backend may be waking up. Will retry...`);
        return null; // Return null to allow polling to continue
      }

      return await this.handleResponse(response);
    } catch (error) {
      console.error('Job Status Error:', error);
      throw error;
    }
  }

  /**
   * Poll job status until completion
   * Handles backend cold starts and 404s gracefully
   */
  async pollJobUntilComplete(
    jobId: string,
    onProgress?: (progress: number, stage: string) => void,
    pollInterval: number = 2000, // Increased from 1s to 2s to reduce server load
    maxAttempts: number = 600 // Increased from 300 to 600 (20 minutes total)
  ): Promise<JobStatusResponse> {
    let attempts = 0;
    let consecutive404s = 0;
    const MAX_CONSECUTIVE_404S = 30; // Allow 30 consecutive 404s (1 minute) for cold starts

    console.log(`[Job Polling] Starting to poll job ${jobId} (max ${maxAttempts} attempts, ${pollInterval}ms interval)`);

    while (attempts < maxAttempts) {
      try {
        const statusResponse = await this.getJobStatus(jobId);

        // Handle null response (404 - job not found)
        if (statusResponse === null) {
          consecutive404s++;
          console.warn(`[Job Polling] Job not found (404) - attempt ${consecutive404s}/${MAX_CONSECUTIVE_404S}. Backend may be cold starting...`);

          // If too many consecutive 404s, something is wrong
          if (consecutive404s >= MAX_CONSECUTIVE_404S) {
            throw new Error(`Job not found after ${consecutive404s} attempts. The job may have been deleted or never existed.`);
          }

          // Wait and retry
          await new Promise(resolve => setTimeout(resolve, pollInterval));
          attempts++;
          continue;
        }

        // Reset 404 counter on successful response
        consecutive404s = 0;

        // Call progress callback if provided
        if (onProgress && statusResponse.data) {
          onProgress(statusResponse.data.progress, statusResponse.data.current_stage);
          console.log(`[Job Polling] Progress: ${statusResponse.data.progress}% - ${statusResponse.data.current_stage}`);
        }

        // Check if job is complete
        if (statusResponse.data.status === 'completed') {
          console.log(`[Job Polling] ✅ Job ${jobId} completed successfully after ${attempts} attempts`);
          return statusResponse;
        }

        // Check if job failed
        if (statusResponse.data.status === 'failed') {
          console.error(`[Job Polling] ❌ Job ${jobId} failed:`, statusResponse.data.error);
          const error = statusResponse.data.error;
          if (typeof error === 'object' && error !== null) {
            // Structured error object from backend
            const errorObj = new Error(error.message || 'Job processing failed');
            (errorObj as any).code = error.code;
            (errorObj as any).type = error.type;
            throw errorObj;
          } else {
            // Simple string error
            throw new Error(error || 'Job processing failed');
          }
        }

        // Job still processing, wait before next poll
        await new Promise(resolve => setTimeout(resolve, pollInterval));
        attempts++;

      } catch (error) {
        // If it's one of our custom errors, throw it
        if (error instanceof Error && (
          (error as any).code ||
          (error as any).type ||
          error.message.includes('Job not found after') ||
          error.message.includes('Job processing failed')
        )) {
          throw error;
        }

        // For network errors, log and retry
        console.error(`[Job Polling] Network error on attempt ${attempts}:`, error);
        await new Promise(resolve => setTimeout(resolve, pollInterval));
        attempts++;
      }
    }

    // If we've exhausted all attempts, check one last time if data exists in database
    console.warn(`[Job Polling] ⚠️ Polling timeout after ${attempts} attempts. Job may have completed - check dashboard.`);
    throw new Error('Job polling timeout - processing may have completed. Please refresh the dashboard to see if your data was saved.');
  }

  /**
   * Extract health metrics synchronously (backward compatibility - does NOT save to database)
   */
  async extractHealthMetrics(file: File, password?: string): Promise<ExtractResponse> {
    const formData = new FormData();
    formData.append('file', file);

    if (password) {
      formData.append('password', password);
    }

    try {
      const response = await fetch(`${this.baseUrl}/extract`, {
        method: 'POST',
        headers: {
          ...this.getAuthHeaders()
        },
        body: formData,
      });

      const data = await this.handleResponse(response);

      if (!data.success) {
        throw new Error(data.error?.message || 'Processing failed');
      }

      return data;
    } catch (error) {
      console.error('API Error:', error);
      throw error;
    }
  }

  /**
   * Check system connectivity
   */
  async checkConnectivity(): Promise<any> {
    try {
      const response = await fetch(`${this.baseUrl}/system/connectivity`);
      return await response.json();
    } catch (error) {
      console.error('Connectivity check failed:', error);
      throw error;
    }
  }

  /**
   * Check basic health
   */
  async checkHealth(): Promise<any> {
    try {
      const response = await fetch(`${this.baseUrl}/health`);
      return await response.json();
    } catch (error) {
      console.error('Health check failed:', error);
      throw error;
    }
  }

  /**
   * Get system health details
   */
  async getSystemHealth(): Promise<any> {
    try {
      const response = await fetch(`${this.baseUrl}/system/health`);
      return await response.json();
    } catch (error) {
      console.error('System health check failed:', error);
      throw error;
    }
  }

  /**
   * Get user health summary (health_summary and overall_health_score)
   */
  async getUserHealthSummary(): Promise<any> {
    try {
      // Add cache-busting timestamp to prevent stale data
      const url = `${this.baseUrl}/user/health-summary?_t=${Date.now()}`;
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          ...this.getAuthHeaders(),
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache'
        }
      });

      return await this.handleResponse(response);
    } catch (error) {
      console.error('Get health summary error:', error);
      throw error;
    }
  }

  /**
   * Get user's language preference
   */
  async getLanguagePreference(): Promise<any> {
    try {
      console.log('[API] 🌐 Fetching language preference...');
      const response = await fetch(`${this.baseUrl}/user/preferences/language`, {
        method: 'GET',
        headers: {
          ...this.getAuthHeaders()
        }
      });
      const result = await this.handleResponse(response);
      console.log('[API] 🌐 Language preference response:', result);
      return result;
    } catch (error) {
      console.error('[API] ❌ Get language preference error:', error);
      throw error;
    }
  }

  /**
   * Update user's language preference
   */
  async updateLanguagePreference(language: string): Promise<any> {
    try {
      console.log('[API] 🌐 Updating language preference to:', language);
      const response = await fetch(`${this.baseUrl}/user/preferences/language`, {
        method: 'PUT',
        headers: {
          ...this.getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ language })
      });
      const result = await this.handleResponse(response);
      console.log('[API] 🌐 Language preference update response:', result);
      return result;
    } catch (error) {
      console.error('[API] ❌ Update language preference error:', error);
      throw error;
    }
  }

  /**
   * Get category insights (AI insights for each category)
   */
  async getCategoryInsights(): Promise<any> {
    try {
      // Add cache-busting timestamp to prevent stale data
      const url = `${this.baseUrl}/user/category-insights?_t=${Date.now()}`;
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          ...this.getAuthHeaders(),
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache'
        }
      });

      return await this.handleResponse(response);
    } catch (error) {
      console.error('Get category insights error:', error);
      throw error;
    }
  }

  /**
   * Get metrics by category
   */
  async getMetricsByCategory(categoryName: string): Promise<any> {
    try {
      const response = await fetch(`${this.baseUrl}/user/categories/${encodeURIComponent(categoryName)}/metrics`, {
        method: 'GET',
        headers: {
          ...this.getAuthHeaders()
        }
      });

      return await this.handleResponse(response);
    } catch (error) {
      console.error('Get metrics by category error:', error);
      throw error;
    }
  }

  /**
   * Get user's prescriptions list
   */
  async getUserPrescriptions(limit: number = 50): Promise<any> {
    try {
      const response = await fetch(`${this.baseUrl}/user/prescriptions?limit=${limit}`, {
        method: 'GET',
        headers: {
          ...this.getAuthHeaders()
        }
      });

      return await this.handleResponse(response);
    } catch (error) {
      console.error('Get prescriptions error:', error);
      throw error;
    }
  }

  /**
   * Get specific prescription details by ID
   */
  async getPrescriptionById(prescriptionId: string): Promise<any> {
    try {
      const response = await fetch(`${this.baseUrl}/prescription/${prescriptionId}`, {
        method: 'GET',
        headers: {
          ...this.getAuthHeaders()
        }
      });

      return await this.handleResponse(response);
    } catch (error) {
      console.error('Get prescription details error:', error);
      throw error;
    }
  }

  /**
   * Upload and process prescription document
   */
  async uploadPrescription(file: File, password?: string): Promise<any> {
    try {
      const formData = new FormData();
      formData.append('file', file);

      if (password) {
        formData.append('password', password);
      }

      const response = await fetch(`${this.baseUrl}/prescription/upload`, {
        method: 'POST',
        headers: {
          ...this.getAuthHeaders()
        },
        body: formData
      });

      return await this.handleResponse(response);
    } catch (error) {
      console.error('Upload prescription error:', error);
      throw error;
    }
  }

  /**
   * Get medicine explanation (tap-to-expand feature)
   */
  async getMedicineExplanation(medicineId: string): Promise<any> {
    try {
      const response = await fetch(`${this.baseUrl}/prescription/medicine/${medicineId}/explain`, {
        method: 'POST',
        headers: {
          ...this.getAuthHeaders()
        }
      });

      return await this.handleResponse(response);
    } catch (error) {
      console.error('Get medicine explanation error:', error);
      throw error;
    }
  }

  /**
   * Delete all user health data (reports, metrics, insights, profiles, conversations)
   * Note: This does NOT delete the user's account, only health data
   */
  async deleteAllUserData(): Promise<any> {
    try {
      const response = await fetch(`${this.baseUrl}/user/data`, {
        method: 'DELETE',
        headers: {
          ...this.getAuthHeaders()
        }
      });

      return await this.handleResponse(response);
    } catch (error) {
      console.error('Delete all data error:', error);
      throw error;
    }
  }

  /**
   * Delete user account permanently (account + all data)
   * WARNING: This action is irreversible and logs out the user
   */
  async deleteUserAccount(): Promise<any> {
    try {
      const response = await fetch(`${this.baseUrl}/user/account`, {
        method: 'DELETE',
        headers: {
          ...this.getAuthHeaders()
        }
      });

      return await this.handleResponse(response);
    } catch (error) {
      console.error('Delete account error:', error);
      throw error;
    }
  }

  /**
   * Submit user feedback
   * Authentication is optional - works for both logged-in and anonymous users
   */
  async submitFeedback(feedbackData: {
    feedback_type: 'bug' | 'feature_request' | 'general' | 'improvement' | 'complaint' | 'praise';
    subject: string;
    message: string;
    rating?: number;
    page_url?: string;
    screen_resolution?: string;
    device_type?: string;
    email?: string;
    allow_contact?: boolean;
  }): Promise<any> {
    try {
      const response = await fetch(`${this.baseUrl}/feedback`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...this.getAuthHeaders()
        },
        body: JSON.stringify(feedbackData)
      });

      return await this.handleResponse(response);
    } catch (error) {
      console.error('Submit feedback error:', error);
      throw error;
    }
  }

  /**
   * Get user's submitted feedback
   */
  async getUserFeedback(params?: {
    status?: string;
    limit?: number;
    offset?: number;
  }): Promise<any> {
    try {
      const queryParams = new URLSearchParams();
      if (params?.status) queryParams.append('status', params.status);
      if (params?.limit) queryParams.append('limit', params.limit.toString());
      if (params?.offset) queryParams.append('offset', params.offset.toString());

      const queryString = queryParams.toString();
      const url = `${this.baseUrl}/feedback${queryString ? `?${queryString}` : ''}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          ...this.getAuthHeaders()
        }
      });

      return await this.handleResponse(response);
    } catch (error) {
      console.error('Get user feedback error:', error);
      throw error;
    }
  }

}

// Create and export a singleton instance
export const apiService = new ApiService();

// Utility function to handle API errors
export const handleApiError = (error: any): string => {
  if (error?.message) {
    return error.message;
  }

  if (typeof error === 'string') {
    return error;
  }

  return 'An unexpected error occurred';
};